import { checkIsErrorToolOutput } from "@repo/app-types/agents/errorToolOutput";
import type { UIMessage } from "ai";
import { Fragment, type ReactNode } from "react";
import type {
  ChatToolStatus,
  ToolComponent,
  ToolComponentProps,
} from "./chatToolCall.js";
import { MessageThinkingIndicator } from "./messageThinkingIndicator.js";

type AnyUIMessagePart = UIMessage["parts"][number];

/**
 * Tool calls the agent runtime makes for its own bookkeeping, hidden on every
 * chat surface.
 */
const HIDDEN_TOOL_NAMES: ReadonlySet<string> = new Set([
  "updateWorkingMemory",
  // loads agent-facing configuration guidance (skill-style) — internal plumbing the user never acts
  // on, so it stays out of the transcript.
  "getPlaybookConfigurationInstructionsTool",
  "getSignalConfigurationInstructionsTool",
]);

/** Tool renderers keyed by tool name, with a fallback for unmapped tools. */
export interface ChatToolComponents {
  byName?: Record<string, ToolComponent>;
  Fallback?: ToolComponent;
}

export interface ChatMessageRenderTextArgs {
  text: string;
  role: UIMessage["role"];
  /** ID of the message this text part belongs to (stable React keys, etc.). */
  messageId: string;
  /** True while this text part is still streaming in. */
  isStreaming: boolean;
}

export interface ChatMessageRenderReasoningArgs {
  text: string;
  isStreaming: boolean;
}

export interface ChatMessageRenderToolGroupArgs {
  /** The run's tool calls, in order. */
  tools: readonly ToolComponentProps[];
  /**
   * True while any part in the run is producing content: a running call, or
   * reasoning folded into the run.
   */
  isActive: boolean;
  /** The run's parts, rendered as they would be ungrouped. */
  children: ReactNode;
}

export interface ChatMessageProps<TMessage extends UIMessage = UIMessage> {
  message: TMessage;
  /** True when this message is the one currently being streamed by the agent. */
  isStreaming?: boolean;
  className?: string;
  tools?: ChatToolComponents;
  /** Renders a text part. Features supply the bubble + markdown styling. */
  renderText: (args: ChatMessageRenderTextArgs) => ReactNode;
  /**
   * Renders a reasoning part. Defaults to a typing indicator while streaming
   * and nothing once done (matching the previous assistant-ui behavior).
   */
  renderReasoning?: (args: ChatMessageRenderReasoningArgs) => ReactNode;
  /**
   * Wraps a run of two or more tool calls with no text between them (reasoning
   * parts in the run are folded in). Without it every part renders on its own.
   */
  renderToolGroup?: (args: ChatMessageRenderToolGroupArgs) => ReactNode;
  /**
   * Shown while the message is streaming but no part is actively producing
   * content (e.g. between a finished tool call and the next text). Defaults to
   * a bare typing indicator.
   */
  renderTypingIndicator?: () => ReactNode;
}

/**
 * Renders a single AI SDK `UIMessage` by iterating its parts and dispatching
 * each to a renderer. Replaces the per-feature `UserMessage`/`AssistantMessage`
 * + `MessagePrimitive.Parts` boilerplate that every chat surface used to
 * duplicate against assistant-ui primitives.
 */
export function ChatMessage<TMessage extends UIMessage>(
  props: ChatMessageProps<TMessage>,
) {
  const { message, isStreaming = false, tools } = props;

  // Hidden tool parts are invisible to the indicator logic below as well: a
  // running one must not suppress the typing indicator.
  const parts = message.parts.filter((part) => !isHiddenToolPart(part));

  const anyPartActive = parts.some(isPartActive);

  // The flash: when the agent's final text part settles (state flips from
  // "streaming" to "done") the part goes inactive a beat before the message
  // stream actually closes, so `isStreaming` is briefly still true with nothing
  // active — long enough to mount the trailing indicator and yank the layout.
  // We only want that indicator while waiting on *new* content, which always
  // arrives as a fresh part: either there are no parts yet (just submitted) or
  // the last part is a non-text part (e.g. a finished tool call before the next
  // text). A settled trailing text part means the message is wrapping up, so
  // suppress it there.
  const lastPart = parts.at(-1);
  const endsWithSettledText =
    lastPart?.type === "text" && lastPart.state !== "streaming";

  const renderPart = (part: AnyUIMessagePart, index: number): ReactNode => {
    const key = `${message.id}-${index}`;

    if (part.type === "text") {
      // While streaming, skip an empty text part — the trailing typing
      // indicator stands in for it (avoids a flashing empty bubble).
      if (checkTextPartSkipped(part.text, isStreaming)) {
        return null;
      }
      return (
        <Fragment key={key}>
          {props.renderText({
            text: part.text,
            role: message.role,
            messageId: message.id,
            isStreaming: part.state === "streaming",
          })}
        </Fragment>
      );
    }

    if (part.type === "reasoning") {
      const reasoningIsStreaming = part.state === "streaming";
      if (props.renderReasoning) {
        return (
          <Fragment key={key}>
            {props.renderReasoning({
              text: part.text,
              isStreaming: reasoningIsStreaming,
            })}
          </Fragment>
        );
      }
      return reasoningIsStreaming ? (
        <MessageThinkingIndicator key={key} />
      ) : null;
    }

    const toolPart = getToolPart(part);
    if (toolPart) {
      const Component = tools?.byName?.[toolPart.toolName] ?? tools?.Fallback;
      if (!Component) {
        return null;
      }
      return <Component key={key} {...toolPart} />;
    }

    // source-url / source-document / file / data-* / step-start are not
    // rendered by these chat surfaces today.
    return null;
  };

  const { renderToolGroup } = props;

  return (
    <>
      {renderToolGroup
        ? buildPartRuns(parts, isStreaming).flatMap<ReactNode>((run) => {
            const rendered = run.map(({ part, index }) =>
              renderPart(part, index),
            );
            const runTools = run.flatMap(({ part }) => {
              const toolPart = getToolPart(part);
              return toolPart ? [toolPart] : [];
            });
            if (runTools.length < 2) {
              return rendered;
            }
            return [
              // Keyed by the run's first part, so the group survives growing.
              <Fragment key={`${message.id}-group-${run[0]?.index}`}>
                {renderToolGroup({
                  tools: runTools,
                  isActive: run.some(({ part }) => isPartActive(part)),
                  children: rendered,
                })}
              </Fragment>,
            ];
          })
        : parts.map(renderPart)}

      {isStreaming && !anyPartActive && !endsWithSettledText
        ? (props.renderTypingIndicator?.() ?? <MessageThinkingIndicator />)
        : null}
    </>
  );
}

interface IndexedPart {
  part: AnyUIMessagePart;
  index: number;
}

/**
 * Splits parts into runs at each text part that renders; the text part is a
 * run of its own. Everything else (tool calls, reasoning, unrendered parts
 * such as `step-start`) stays in the run around it.
 */
function buildPartRuns(
  parts: readonly AnyUIMessagePart[],
  isStreaming: boolean,
): IndexedPart[][] {
  const runs: IndexedPart[][] = [];
  let current: IndexedPart[] = [];

  parts.forEach((part, index) => {
    if (part.type === "text" && !checkTextPartSkipped(part.text, isStreaming)) {
      if (current.length > 0) {
        runs.push(current);
      }
      runs.push([{ part, index }]);
      current = [];
    } else {
      current.push({ part, index });
    }
  });

  if (current.length > 0) {
    runs.push(current);
  }
  return runs;
}

function checkTextPartSkipped(text: string, isStreaming: boolean): boolean {
  return isStreaming && text.trim() === "";
}

interface NormalizedToolPart {
  toolName: string;
  toolCallId: string;
  input: unknown;
  output: unknown;
  errorText: string | undefined;
  status: ChatToolStatus;
}

/** Normalize an AI SDK tool part (`tool-${name}` or `dynamic-tool`). */
function getToolPart(part: AnyUIMessagePart): NormalizedToolPart | null {
  const toolName =
    part.type === "dynamic-tool"
      ? part.toolName
      : part.type.startsWith("tool-")
        ? part.type.slice("tool-".length)
        : null;

  if (toolName === null) {
    return null;
  }

  // All tool parts share these fields across their state union.
  const toolPart = part as Extract<AnyUIMessagePart, { toolCallId: string }> & {
    state: string;
    input?: unknown;
    output?: unknown;
    errorText?: string;
  };

  return {
    toolName,
    toolCallId: toolPart.toolCallId,
    // While the input streams in, `input` holds the partially parsed object
    // (or nothing at all yet). Default it to an object so tool components'
    // flat field reads (`input.title`) see `undefined` fields, not a crash —
    // letting them show their friendly running label from the first chunk.
    input: toolPart.input ?? {},
    output: toolPart.output,
    errorText: toolPart.errorText,
    status: toolStatus(toolPart.state, toolPart.output),
  };
}

function toolStatus(state: string, output: unknown): ChatToolStatus {
  if (state === "output-available") {
    // A tool that catches its own failure still completes, so the model sees
    // the error and carries on; only the display marks it as failed.
    return checkIsErrorToolOutput(output)
      ? { type: "incomplete" }
      : { type: "complete" };
  }
  if (state === "output-error" || state === "output-denied") {
    return { type: "incomplete" };
  }
  return { type: "running" };
}

function isHiddenToolPart(part: AnyUIMessagePart): boolean {
  const toolPart = getToolPart(part);
  return toolPart !== null && HIDDEN_TOOL_NAMES.has(toolPart.toolName);
}

/** Whether a part is actively producing content (drives the typing indicator). */
function isPartActive(part: AnyUIMessagePart): boolean {
  if (part.type === "text" || part.type === "reasoning") {
    return part.state === "streaming";
  }
  const toolPart = getToolPart(part);
  return toolPart?.status.type === "running";
}
